import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { Card } from 'primereact/card';
import { Button } from 'primereact/button';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Chart } from 'primereact/chart';
import { InputText } from 'primereact/inputtext';
import { Dropdown } from 'primereact/dropdown';
import { Dialog } from 'primereact/dialog';
import { Toast } from 'primereact/toast';
import { Message } from 'primereact/message';
import { Skeleton } from 'primereact/skeleton';
import { Avatar } from 'primereact/avatar';
import { Tag } from 'primereact/tag';
import { Accordion, AccordionTab } from 'primereact/accordion';
import { ProgressBar } from 'primereact/progressbar';
import { Paginator } from 'primereact/paginator';
import { IconField } from 'primereact/iconfield';
import { InputIcon } from 'primereact/inputicon';
import storageUsageService from '../../services/storageUsageService';

// Chart.js needs concrete colors at render — scoped to chart data only.
const CHART_COLORS = ['#3B82F6', '#22C55E', '#F59E0B', '#EF4444', '#8B5CF6', '#06B6D4', '#EC4899', '#6366F1', '#14B8A6', '#F97316'];

const SECTION_LABEL = {
    leads: 'Leads',
    reports: 'Reports',
    profile: 'Profile Documents',
    bank_statements: 'Bank Statements',
    profiles: 'Profile Photos',
    feed: 'Social Feed',
    marketplace: 'Marketplace',
    conversions: 'Conversions',
    messaging: 'Messaging',
    establishment: 'Establishment',
};

const SECTION_ICON = {
    leads: 'pi-users',
    reports: 'pi-file',
    profile: 'pi-id-card',
    bank_statements: 'pi-wallet',
    profiles: 'pi-image',
    feed: 'pi-comments',
    marketplace: 'pi-shopping-cart',
    conversions: 'pi-sync',
    messaging: 'pi-envelope',
    establishment: 'pi-building',
};

const getSectionLabel = (s) => SECTION_LABEL[s] || s;
const getSectionIcon = (s) => SECTION_ICON[s] || 'pi-folder';

// KPI card (P15)
const KpiCard = ({ icon, label, value, description, loading }) => (
    <div className="col-6 lg:col-3">
        <Card className="h-full shadow-1" pt={{ body: { className: 'p-3' } }}>
            <div className="flex justify-content-between align-items-start">
                <div className="flex flex-column gap-1">
                    <span className="text-600 text-sm font-medium">{label}</span>
                    <span className="text-900 text-xl font-semibold">
                        {loading ? <Skeleton width="4rem" height="1.5rem" /> : value}
                    </span>
                </div>
                <div className="flex align-items-center justify-content-center border-round bg-primary-50 p-2">
                    <i className={`pi ${icon} text-xl text-primary`} aria-hidden="true"></i>
                </div>
            </div>
            <span className="text-500 text-xs block mt-2">{description}</span>
        </Card>
    </div>
);

// Mobile AccordionTab header — employee primary info + storage tag + view button
const EmployeeHeader = ({ employee, onView }) => (
    <div className="flex justify-content-between align-items-center gap-2 w-full">
        <div className="flex align-items-center gap-2 flex-1 min-w-0">
            <Avatar
                label={employee.name?.charAt(0)?.toUpperCase() || '?'}
                shape="circle"
                className="flex-shrink-0"
            />
            <div className="flex flex-column min-w-0">
                <span
                    className="font-medium text-sm text-900 white-space-nowrap overflow-hidden text-overflow-ellipsis"
                    title={employee.name}
                >
                    {employee.name || '—'}
                </span>
                <span
                    className="text-xs text-500 white-space-nowrap overflow-hidden text-overflow-ellipsis"
                    title={employee.email}
                >
                    {employee.email || '—'}
                </span>
            </div>
        </div>
        <div className="flex align-items-center gap-1 flex-shrink-0">
            <Tag
                value={employee.total_formatted || '0 B'}
                severity={employee.total_bytes > 0 ? 'info' : 'secondary'}
                className="text-xs"
            />
            <div onClick={(e) => e.stopPropagation()}>
                <Button
                    icon="pi pi-eye"
                    rounded
                    text
                    size="small"
                    aria-label="View storage details"
                    onClick={() => onView(employee)}
                />
            </div>
        </div>
    </div>
);

// Mobile AccordionTab content — mirrors all desktop columns
const EmployeeDetails = ({ employee }) => (
    <div className="flex flex-column gap-2 text-xs">
        <div className="flex align-items-center gap-2">
            <i className="pi pi-hashtag text-500 w-1rem" aria-hidden="true" />
            <span className="text-500 font-medium w-6rem">ID</span>
            <span className="text-700">#{employee.id}</span>
        </div>
        <div className="flex align-items-center gap-2">
            <i className="pi pi-file text-500 w-1rem" aria-hidden="true" />
            <span className="text-500 font-medium w-6rem">Files</span>
            <span className="text-700">{employee.total_files?.toLocaleString() || '0'}</span>
        </div>
        <div className="flex align-items-center gap-2">
            <i className="pi pi-clock text-500 w-1rem" aria-hidden="true" />
            <span className="text-500 font-medium w-6rem">Last Upload</span>
            <span className={employee.last_upload_at ? 'text-700' : 'text-500'}>
                {employee.last_upload_at ? storageUsageService.formatDate(employee.last_upload_at) : '—'}
            </span>
        </div>
    </div>
);

const CompanyStorageUsage = () => {
    const toast = useRef(null);

    // Access control
    const userRoles = JSON.parse(localStorage.getItem('userRoles') || '[]');
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    const primaryRole = userRoles.length > 0 ? userRoles[0] : (user.user_type || 'user');
    const isCompanyAdmin = ['company_owner', 'company_admin'].includes(primaryRole) || !!user.company_id;

    // Data
    const [data, setData] = useState(null);
    const [employees, setEmployees] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [quota, setQuota] = useState(null);

    // Pagination + sort
    const [totalRecords, setTotalRecords] = useState(0);
    const [first, setFirst] = useState(0);
    const [rows, setRows] = useState(10);
    const [sortField, setSortField] = useState('name');
    const [sortOrder, setSortOrder] = useState(1);

    // Filters
    const [usersList, setUsersList] = useState([]);
    const [selectedUser, setSelectedUser] = useState(null);
    const [loadingUsers, setLoadingUsers] = useState(false);
    const [search, setSearch] = useState('');
    const [debouncedSearch, setDebouncedSearch] = useState('');

    // Mobile accordion
    const [mobileActiveIndex, setMobileActiveIndex] = useState(null);

    // Detail dialog
    const [detailVisible, setDetailVisible] = useState(false);
    const [detailEmployee, setDetailEmployee] = useState(null);
    const [detailData, setDetailData] = useState(null);
    const [detailLoading, setDetailLoading] = useState(false);

    // Refresh nonce
    const [refreshNonce, setRefreshNonce] = useState(0);

    // Debounced search (P13)
    useEffect(() => {
        const timer = setTimeout(() => {
            setDebouncedSearch(search.trim());
            setFirst(0);
        }, 500);
        return () => clearTimeout(timer);
    }, [search]);

    // Load employees-for-filter once
    useEffect(() => {
        if (!isCompanyAdmin) return;
        const fetchUsers = async () => {
            setLoadingUsers(true);
            try {
                const response = await storageUsageService.getCompanyUsersForFilter({
                    limit: 100,
                    order: 'name_asc',
                });
                if (response.success) setUsersList(response.data || []);
            } catch (err) {
                console.error('Failed to load employees:', err?.message || 'Unknown error');
            } finally {
                setLoadingUsers(false);
            }
        };
        fetchUsers();
    }, [isCompanyAdmin]);

    // Load the company storage quota (plan limit vs shared-pool usage). It's
    // company-wide — independent of filters/pagination — so fetch on mount and
    // on explicit refresh only. Best-effort: a failure must not block the page.
    useEffect(() => {
        if (!isCompanyAdmin) return;
        let cancelled = false;
        (async () => {
            try {
                const res = await storageUsageService.getQuota();
                if (!cancelled && res.success) setQuota(res.data);
            } catch (err) {
                console.error('Failed to load storage quota:', err?.message || 'Unknown error');
            }
        })();
        return () => { cancelled = true; };
    }, [isCompanyAdmin, refreshNonce]);

    // Load usage whenever primitive filter/page/sort deps change (P14)
    useEffect(() => {
        if (!isCompanyAdmin) return;
        let cancelled = false;
        const loadData = async () => {
            setLoading(true);
            setError(null);
            try {
                const page = Math.floor(first / rows) + 1;
                const params = {
                    page,
                    limit: rows,
                    sort_field: sortField,
                    sort_order: sortOrder === 1 ? 'asc' : 'desc',
                };
                if (selectedUser) params.user_id = selectedUser;
                if (debouncedSearch) params.search = debouncedSearch;

                const response = await storageUsageService.getCompanyUsage(params);
                if (cancelled) return;
                if (response.success && response.data) {
                    setData(response.data);
                    setEmployees(response.data.users || []);
                    setTotalRecords(response.data.pagination?.total || 0);
                } else {
                    setEmployees([]);
                    setTotalRecords(0);
                }
            } catch (err) {
                if (!cancelled) {
                    console.error('Failed to load company storage data:', err?.message || 'Unknown error');
                    setError('Failed to load company storage usage data.');
                    setEmployees([]);
                    setTotalRecords(0);
                }
            } finally {
                if (!cancelled) setLoading(false);
            }
        };
        loadData();
        return () => { cancelled = true; };
    }, [isCompanyAdmin, first, rows, sortField, sortOrder, selectedUser, debouncedSearch, refreshNonce]);

    // Reset mobile expanded row when result set changes (P17)
    useEffect(() => {
        setMobileActiveIndex(null);
    }, [first, selectedUser, debouncedSearch]);

    const clearFilters = () => {
        setSearch('');
        setSelectedUser(null);
        setFirst(0);
    };

    const refresh = () => setRefreshNonce((n) => n + 1);

    const openEmployeeDetail = useCallback(async (rowData) => {
        setDetailEmployee(rowData);
        setDetailData(null);
        setDetailVisible(true);
        setDetailLoading(true);
        try {
            const response = await storageUsageService.getUserUsage(rowData.id);
            if (response.success) setDetailData(response.data);
        } catch (err) {
            console.error('Failed to load employee usage:', err?.message || 'Unknown error');
            toast.current?.show({
                severity: 'error',
                summary: 'Error',
                detail: 'Failed to load employee storage details',
            });
        } finally {
            setDetailLoading(false);
        }
    }, []);

    // Chart data
    const chartData = useMemo(() => {
        const sections = data?.summary?.by_section;
        if (!sections || sections.length === 0) return null;
        return {
            labels: sections.map((s) => getSectionLabel(s.section)),
            datasets: [{
                data: sections.map((s) => s.bytes),
                backgroundColor: CHART_COLORS.slice(0, sections.length),
                borderWidth: 0,
                hoverOffset: 6,
            }],
        };
    }, [data]);

    const detailChartData = useMemo(() => {
        const sections = detailData?.by_section;
        if (!sections || sections.length === 0) return null;
        return {
            labels: sections.map((s) => getSectionLabel(s.section)),
            datasets: [{
                data: sections.map((s) => s.bytes),
                backgroundColor: CHART_COLORS.slice(0, sections.length),
                borderWidth: 0,
                hoverOffset: 6,
            }],
        };
    }, [detailData]);

    // Doughnut options — compact, no built-in legend (we render adjacent breakdown)
    const chartOptions = useMemo(() => ({
        responsive: true,
        maintainAspectRatio: true,
        cutout: '65%',
        plugins: {
            legend: { display: false },
            tooltip: {
                callbacks: {
                    label: (ctx) => {
                        const value = storageUsageService.formatBytes(ctx.raw);
                        const total = ctx.dataset.data.reduce((a, b) => a + b, 0);
                        const pct = total > 0 ? ((ctx.raw / total) * 100).toFixed(1) : '0.0';
                        return ` ${value} (${pct}%)`;
                    },
                },
            },
        },
    }), []);

    const sections = data?.summary?.by_section || [];
    const detailSections = detailData?.by_section || [];
    const detailTotalBytes = detailData?.total_bytes || 0;

    const avgPerEmployee = useMemo(() => {
        const tb = data?.summary?.total_bytes || 0;
        const ec = totalRecords || 1;
        return storageUsageService.formatBytes(Math.round(tb / ec));
    }, [data, totalRecords]);

    // Access denied — after hooks, before render
    if (!isCompanyAdmin) {
        return (
            <>
                <div className="grid">
                    <div className="col-12">
                        <div className="mb-4">
                            <h1 className="text-xl font-semibold text-900 m-0 mb-1">Company Storage Usage</h1>
                            <p className="text-600 text-sm m-0">Monitor storage across your company employees</p>
                        </div>
                        <Card>
                            <Message severity="error" text="You need company admin privileges to access this page." className="w-full" />
                        </Card>
                    </div>
                </div>
            </>
        );
    }

    // Column templates
    const idBodyTemplate = (rowData) => <span className="text-sm text-500">#{rowData.id}</span>;

    const nameBodyTemplate = (rowData) => (
        <div className="flex align-items-center gap-2">
            <Avatar
                label={rowData.name?.charAt(0)?.toUpperCase() || '?'}
                shape="circle"
                size="normal"
                className="flex-shrink-0"
            />
            <div className="flex flex-column">
                <span className="font-medium text-sm text-900">{rowData.name}</span>
                <span className="text-xs text-500">{rowData.email}</span>
            </div>
        </div>
    );

    const storageBodyTemplate = (rowData) => (
        <span className="font-semibold text-sm text-primary">{rowData.total_formatted || '0 B'}</span>
    );

    const filesBodyTemplate = (rowData) => (
        <span className="text-sm text-700">{rowData.total_files?.toLocaleString() || '0'}</span>
    );

    const lastUploadBodyTemplate = (rowData) => (
        <span className={`text-sm ${rowData.last_upload_at ? 'text-700' : 'text-500'}`}>
            {rowData.last_upload_at ? storageUsageService.formatDate(rowData.last_upload_at) : '—'}
        </span>
    );

    const actionBodyTemplate = (rowData) => (
        <Button
            icon="pi pi-eye"
            severity="info"
            outlined
            size="small"
            aria-label="View storage details"
            tooltip="View details"
            tooltipOptions={{ position: 'left' }}
            onClick={() => openEmployeeDetail(rowData)}
        />
    );

    return (
        <>
            <Toast ref={toast} />

            <div className="grid">
                <div className="col-12">
                    {/* Page header (P6) */}
                    <div className="mb-4">
                        <h1 className="text-xl font-semibold text-900 m-0 mb-1">Company Storage Usage</h1>
                        <p className="text-600 text-sm m-0">Monitor storage across your company employees</p>
                    </div>

                    {error && <Message severity="error" text={error} className="w-full mb-3" />}

                    {/* KPI cards (P15) — 4 cards: 2 per row mobile, 4 per row desktop */}
                    <div className="grid mb-3">
                        <KpiCard
                            icon="pi-database"
                            label="Total Storage"
                            value={data?.summary?.total_formatted || '0 B'}
                            description="Across all employees"
                            loading={loading}
                        />
                        <KpiCard
                            icon="pi-file"
                            label="Total Files"
                            value={data?.summary?.total_files?.toLocaleString() || '0'}
                            description="Files uploaded"
                            loading={loading}
                        />
                        <KpiCard
                            icon="pi-users"
                            label="Total Employees"
                            value={totalRecords || 0}
                            description="Employees with storage footprint"
                            loading={loading}
                        />
                        <KpiCard
                            icon="pi-chart-bar"
                            label="Avg / Employee"
                            value={avgPerEmployee}
                            description="Average storage per employee"
                            loading={loading}
                        />
                    </div>

                    {/* Plan Storage quota — shared company pool vs the owner's plan limit */}
                    {quota && !quota.unlimited && quota.limit_bytes && (() => {
                        const pct = Math.min(100, Math.round(quota.percent));
                        const barColor = pct >= 100
                            ? 'var(--red-500)'
                            : (quota.warn ? 'var(--orange-500)' : 'var(--green-500)');
                        return (
                            <Card className="mb-3">
                                <div className="flex align-items-center justify-content-between mb-2 flex-wrap gap-2">
                                    <h2 className="text-base font-medium text-900 m-0 flex align-items-center gap-2">
                                        <i className="pi pi-server text-primary" aria-hidden="true" />
                                        Plan Storage
                                    </h2>
                                    <span className="text-sm text-700">
                                        <span className="font-semibold text-900">{quota.used_formatted}</span>
                                        {' of '}
                                        <span className="font-semibold text-900">{quota.limit_formatted}</span>
                                        <span className="text-500 ml-2">({pct}%)</span>
                                    </span>
                                </div>
                                <ProgressBar
                                    value={pct}
                                    showValue={false}
                                    style={{ height: '8px' }}
                                    color={barColor}
                                    aria-label="Company plan storage used"
                                />
                                {pct >= 100 ? (
                                    <Message
                                        severity="error"
                                        text="Your company storage is full. Employees cannot upload until you free up space or upgrade the plan."
                                        className="w-full mt-2"
                                    />
                                ) : quota.warn ? (
                                    <Message
                                        severity="warn"
                                        text={`Your company has used over ${quota.warn_threshold_percent}% of its storage. Consider upgrading soon to avoid upload interruptions.`}
                                        className="w-full mt-2"
                                    />
                                ) : null}
                            </Card>
                        );
                    })()}

                    {/* Storage Distribution — small doughnut + breakdown list */}
                    {!loading && chartData && sections.length > 0 && (
                        <Card className="mb-3">
                            <div className="flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
                                <h2 className="text-base font-medium text-900 m-0">Storage Distribution</h2>
                            </div>
                            <div className="flex flex-column md:flex-row align-items-center md:align-items-start gap-4">
                                <div className="flex-shrink-0" style={{ width: '180px', height: '180px' }}>
                                    <Chart
                                        type="doughnut"
                                        data={chartData}
                                        options={chartOptions}
                                        className="w-full h-full"
                                    />
                                </div>
                                <div className="flex-grow-1 w-full">
                                    <div className="flex flex-column gap-2">
                                        {sections.map((s, i) => {
                                            const total = data?.summary?.total_bytes || 0;
                                            const pct = total > 0 ? ((s.bytes / total) * 100).toFixed(1) : '0.0';
                                            return (
                                                <div
                                                    key={s.section}
                                                    className="flex align-items-center justify-content-between gap-2 border-bottom-1 surface-border pb-2"
                                                >
                                                    <div className="flex align-items-center gap-2 min-w-0">
                                                        <span
                                                            className="border-circle flex-shrink-0"
                                                            style={{
                                                                width: '10px',
                                                                height: '10px',
                                                                backgroundColor: CHART_COLORS[i % CHART_COLORS.length],
                                                            }}
                                                            aria-hidden="true"
                                                        />
                                                        <span className="text-sm text-700 white-space-nowrap overflow-hidden text-overflow-ellipsis">
                                                            {getSectionLabel(s.section)}
                                                        </span>
                                                    </div>
                                                    <div className="flex align-items-center gap-2 flex-shrink-0">
                                                        <span className="text-sm font-semibold text-900">{s.formatted}</span>
                                                        <span className="text-xs text-500 w-3rem text-right">{pct}%</span>
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                </div>
                            </div>
                        </Card>
                    )}

                    <Card>
                        {/* Filter bar (P7 — 2 filter pattern) — INSIDE Card as P20 requires */}
                        <div className="surface-ground p-3 border-round mb-3">
                            <div className="grid">
                                <div className="col-6 md:col">
                                    <Dropdown
                                        value={selectedUser}
                                        options={usersList}
                                        onChange={(e) => { setSelectedUser(e.value); setFirst(0); }}
                                        optionLabel="name"
                                        optionValue="id"
                                        filter
                                        filterBy="name,email"
                                        placeholder="All Employees"
                                        showClear
                                        className="w-full p-inputtext-sm"
                                        loading={loadingUsers}
                                        emptyMessage="No employees found"
                                        aria-label="Filter by employee"
                                    />
                                </div>
                                <div className="col-6 md:col">
                                    <IconField iconPosition="left" className="w-full">
                                        <InputIcon className="pi pi-search" />
                                        <InputText
                                            value={search}
                                            onChange={(e) => setSearch(e.target.value)}
                                            placeholder="Search by name, email or mobile"
                                            maxLength={100}
                                            className="w-full p-inputtext-sm"
                                            aria-label="Search employees"
                                        />
                                    </IconField>
                                </div>
                                <div className="hidden md:block md:col" aria-hidden="true"></div>
                                <div className="hidden md:block md:col" aria-hidden="true"></div>
                                <div className="col-12 md:col flex gap-2">
                                    <Button
                                        label="Clear"
                                        icon="pi pi-filter-slash"
                                        severity="secondary"
                                        outlined
                                        size="small"
                                        className="flex-1 py-1 md:py-2"
                                        onClick={clearFilters}
                                        disabled={!search && !selectedUser}
                                    />
                                    <Button
                                        label="Refresh"
                                        icon="pi pi-refresh"
                                        size="small"
                                        className="flex-1 py-1 md:py-2"
                                        onClick={refresh}
                                        loading={loading}
                                    />
                                </div>
                            </div>
                        </div>

                        {/* Mobile (< md): Accordion cards */}
                        <div className="block md:hidden">
                            {loading && (
                                <div className="flex flex-column gap-2">
                                    <Skeleton height="70px" />
                                    <Skeleton height="70px" />
                                    <Skeleton height="70px" />
                                </div>
                            )}
                            {!loading && employees.length === 0 && (
                                <div className="flex flex-column align-items-center py-5">
                                    <i className="pi pi-inbox pi-2x text-400 mb-3" aria-hidden="true" />
                                    <span className="text-600">No storage usage data found</span>
                                </div>
                            )}
                            {!loading && employees.length > 0 && (
                                <>
                                    <Accordion
                                        activeIndex={mobileActiveIndex}
                                        onTabChange={(e) => setMobileActiveIndex(e.index)}
                                    >
                                        {employees.map((emp) => (
                                            <AccordionTab
                                                key={`emp-${emp.id}`}
                                                header={<EmployeeHeader employee={emp} onView={openEmployeeDetail} />}
                                            >
                                                <EmployeeDetails employee={emp} />
                                            </AccordionTab>
                                        ))}
                                    </Accordion>
                                    <Paginator
                                        first={first}
                                        rows={rows}
                                        totalRecords={totalRecords}
                                        onPageChange={(e) => { setFirst(e.first); setRows(e.rows); }}
                                        template="PrevPageLink PageLinks NextPageLink CurrentPageReport"
                                        currentPageReportTemplate="{first}-{last} of {totalRecords}"
                                        pageLinkSize={3}
                                        className="text-sm mt-2"
                                    />
                                </>
                            )}
                        </div>

                        {/* Desktop (≥ md): DataTable */}
                        <div className="hidden md:block">
                            <DataTable
                                value={employees}
                                dataKey="id"
                                lazy
                                loading={loading}
                                paginator
                                first={first}
                                rows={rows}
                                totalRecords={totalRecords}
                                onPage={(e) => { setFirst(e.first); setRows(e.rows); }}
                                sortField={sortField}
                                sortOrder={sortOrder}
                                onSort={(e) => { setSortField(e.sortField); setSortOrder(e.sortOrder); setFirst(0); }}
                                paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink RowsPerPageDropdown CurrentPageReport"
                                currentPageReportTemplate="Showing {first}-{last} of {totalRecords}"
                                paginatorClassName="text-sm"
                                pageLinkSize={3}
                                rowsPerPageOptions={[10, 25, 50, 100]}
                                responsiveLayout="scroll"
                                scrollable
                                stripedRows
                                showGridlines
                                size="small"
                                className="p-datatable-gridlines"
                                emptyMessage={
                                    <div className="flex flex-column align-items-center py-5">
                                        <i className="pi pi-inbox pi-2x text-400 mb-3" aria-hidden="true" />
                                        <span className="text-600">No storage usage data found</span>
                                    </div>
                                }
                            >
                                <Column field="id" header="ID" body={idBodyTemplate} style={{ width: '80px' }} />
                                <Column
                                    field="name"
                                    header="Employee"
                                    body={nameBodyTemplate}
                                    sortable
                                    frozen
                                    alignFrozen="left"
                                    style={{ minWidth: '260px' }}
                                />
                                <Column
                                    field="file_count"
                                    header="Files"
                                    body={filesBodyTemplate}
                                    sortable
                                    style={{ minWidth: '100px' }}
                                />
                                <Column
                                    field="total_bytes"
                                    header="Storage"
                                    body={storageBodyTemplate}
                                    sortable
                                    style={{ minWidth: '120px' }}
                                />
                                <Column
                                    field="last_upload_at"
                                    header="Last Upload"
                                    body={lastUploadBodyTemplate}
                                    sortable
                                    style={{ minWidth: '160px' }}
                                />
                                <Column
                                    header="Actions"
                                    body={actionBodyTemplate}
                                    frozen
                                    alignFrozen="right"
                                    style={{ minWidth: '80px', width: '80px' }}
                                />
                            </DataTable>
                        </div>
                    </Card>
                </div>
            </div>

            {/* Detail dialog (P4) */}
            <Dialog
                visible={detailVisible}
                onHide={() => setDetailVisible(false)}
                modal
                draggable={false}
                dismissableMask
                style={{ width: '90vw', maxWidth: '600px' }}
                header={
                    detailEmployee ? (
                        <div className="flex align-items-center gap-3">
                            <Avatar
                                label={detailEmployee.name?.charAt(0)?.toUpperCase() || '?'}
                                shape="circle"
                                size="large"
                            />
                            <div className="flex flex-column">
                                <span className="font-semibold text-900 text-base">{detailEmployee.name || '—'}</span>
                                <span className="text-500 text-xs">{detailEmployee.email || '—'}</span>
                            </div>
                        </div>
                    ) : 'Employee Storage'
                }
            >
                {/* Summary strip */}
                <div className="grid mb-3">
                    <div className="col-4 text-center">
                        <div className="text-xs text-500 mb-1">Total Storage</div>
                        <div className="font-semibold text-sm text-primary">
                            {detailLoading ? <Skeleton width="3rem" height="1rem" className="mx-auto" /> : (detailData?.total_formatted || '0 B')}
                        </div>
                    </div>
                    <div className="col-4 text-center">
                        <div className="text-xs text-500 mb-1">Total Files</div>
                        <div className="font-semibold text-sm text-900">
                            {detailLoading ? <Skeleton width="2rem" height="1rem" className="mx-auto" /> : (detailData?.total_files?.toLocaleString() || '0')}
                        </div>
                    </div>
                    <div className="col-4 text-center">
                        <div className="text-xs text-500 mb-1">Sections</div>
                        <div className="font-semibold text-sm text-900">
                            {detailLoading ? <Skeleton width="1.5rem" height="1rem" className="mx-auto" /> : detailSections.length}
                        </div>
                    </div>
                </div>

                {detailLoading && (
                    <div className="flex flex-column gap-2">
                        <Skeleton width="100%" height="140px" />
                        <Skeleton width="100%" height="1.5rem" />
                        <Skeleton width="100%" height="1.5rem" />
                        <Skeleton width="100%" height="1.5rem" />
                    </div>
                )}

                {!detailLoading && detailSections.length === 0 && (
                    <div className="flex flex-column align-items-center py-5">
                        <i className="pi pi-inbox pi-2x text-400 mb-3" aria-hidden="true" />
                        <span className="text-600">No storage data available for this employee</span>
                    </div>
                )}

                {!detailLoading && detailSections.length > 0 && (
                    <div className="flex flex-column md:flex-row align-items-center md:align-items-start gap-4">
                        <div className="flex-shrink-0" style={{ width: '160px', height: '160px' }}>
                            <Chart
                                type="doughnut"
                                data={detailChartData}
                                options={chartOptions}
                                className="w-full h-full"
                            />
                        </div>
                        <div className="flex-grow-1 w-full overflow-y-auto" style={{ maxHeight: '280px' }}>
                            <div className="flex flex-column gap-2">
                                {detailSections.map((section, i) => {
                                    const pct = detailTotalBytes > 0 ? ((section.bytes / detailTotalBytes) * 100).toFixed(1) : '0.0';
                                    const color = CHART_COLORS[i % CHART_COLORS.length];
                                    return (
                                        <div
                                            key={section.section}
                                            className="flex align-items-center justify-content-between gap-2 border-bottom-1 surface-border pb-2"
                                        >
                                            <div className="flex align-items-center gap-2 min-w-0">
                                                <span
                                                    className="border-circle flex-shrink-0"
                                                    style={{ width: '10px', height: '10px', backgroundColor: color }}
                                                    aria-hidden="true"
                                                />
                                                <i className={`pi ${getSectionIcon(section.section)} text-500 text-xs flex-shrink-0`} aria-hidden="true" />
                                                <div className="flex flex-column min-w-0">
                                                    <span className="text-sm text-700 white-space-nowrap overflow-hidden text-overflow-ellipsis">
                                                        {getSectionLabel(section.section)}
                                                    </span>
                                                    <span className="text-xs text-500">{section.files} files</span>
                                                </div>
                                            </div>
                                            <div className="flex align-items-center gap-2 flex-shrink-0">
                                                <span className="text-sm font-semibold text-900">{section.formatted}</span>
                                                <span className="text-xs text-500 w-3rem text-right">{pct}%</span>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    </div>
                )}
            </Dialog>
        </>
    );
};

export default CompanyStorageUsage;
